const chapter = {
  id: 'electrostatics',
  slug: 'electrostatics',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Electrostatics',
        hindi: 'स्थिर विद्युत',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'coulomb',
      title: {
        english: 'Coulomb\'s Law & Electric Field',
        hindi: 'कूलॉम का नियम और विद्युत क्षेत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Coulomb\'s law',
        hindi: 'कूलम्ब का नियम',
          },
          statement: {
            english: `Force between two point charges $q_1, q_2$ separated by $r$ in vacuum:
$$\\vec F = \\dfrac{1}{4\\pi\\varepsilon_0}\\dfrac{q_1 q_2}{r^2}\\hat r,\\qquad \\dfrac{1}{4\\pi\\varepsilon_0} = k = 9\\times 10^9\\ \\text{N·m}^2/\\text{C}^2.$$
In medium of relative permittivity $\\varepsilon_r$: $F' = F/\\varepsilon_r$.`,
            hindi: 'निर्वात में $q_1, q_2$ द्वारा अलग किए गए दो बिंदु आवेशों के बीच बल: $$\\vec F = \\dfrac{1}{4\\pi\\varepsilon_0}\\dfrac{q_1 q_2}{r^2}\\hat r,\\qquad \\dfrac{1}{4\\pi\\varepsilon_0} = k = 9\\times 10^9\\\\\\\\ \\text{N·m}^2/\\text{C}^2.$$ सापेक्ष पारगम्यता के माध्यम में $\\varepsilon_r$: $F\\\' = F/\\varepsilon_r$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Electric field',
        hindi: 'विद्युत क्षेत्र',
          },
          body: {
            english: '$\\vec E = \\vec F / q_0$ on a small positive test charge. Unit: N/C = V/m. For point charge: $E = kq/r^2$.',
        hindi: 'एक छोटे से सकारात्मक परीक्षण चार्ज पर $\\vec E = \\vec F / q_0$। यूनिट: N/. पॉइंट चार्ज के लिए: $E = kq/r^2$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Charge is **quantised** ($q = ne$, $e = 1.6\\times 10^{-19}$ C) and **conserved**.',
        hindi: 'चार्ज **मात्राबद्ध** ($q = ne$, $e = 1.6\\times 10^{-19}$ C) और **संरक्षित** है।',
            },
            {
              english: '**Superposition**: net field = vector sum of fields due to each source charge.',
        hindi: '**सुपरपोज़िशन**: शुद्ध फ़ील्ड = प्रत्येक स्रोत चार्ज के कारण फ़ील्ड का वेक्टर योग।',
            },
            {
              english: '**Electric dipole** of moment $\\vec p = q\\vec d$ (from $-q$ to $+q$). Field on axis: $E_{\\text{ax}} = 2kp/r^3$. On equator: $E_{\\text{eq}} = kp/r^3$. ($r \\gg d$.)',
        hindi: '**विद्युत द्विध्रुव** आघूर्ण $\\vec p = q\\vec d$ ($-q$ से $+q$ तक)। अक्ष पर फ़ील्ड: $E_{\\text{ax}} = 2kp/r^3$. भूमध्य रेखा पर: $E_{\\text{eq}} = kp/r^3$. ($r \\gg d$.)',
            },
            {
              english: 'Torque on dipole in field $\\vec E$: $\\vec\\tau = \\vec p \\times \\vec E$. PE: $U = -\\vec p \\cdot \\vec E$.',
        hindi: 'फ़ील्ड $\\vec E$ में द्विध्रुव पर टॉर्क: $\\vec\\tau = \\vec p \\times \\vec E$। पीई: $U = -\\vec p \\cdot \\vec E$.',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Force between two $1\\ \\mu C$ charges 1 m apart in vacuum.',
        hindi: 'निर्वात में 1 मीटर की दूरी पर स्थित दो $1\\\\ \\mu C$ आवेशों के बीच बल।',
          },
          solution: {
            english: '$F = 9\\times 10^9 \\cdot (10^{-6})^2 / 1 = 9\\times 10^{-3}$ N.',
        hindi: '$F = 9\\times 10^9 \\cdot (10^{-6})^2 / 1 = 9\\times 10^{-3}$ एन.',
          },
        },
      ],
    },
    {
      id: 'gauss',
      title: {
        english: 'Electric Flux & Gauss\'s Law',
        hindi: 'विद्युत फ्लक्स और गॉस का नियम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Electric flux',
        hindi: 'विद्युत प्रवाह',
          },
          body: {
            english: '$\\Phi_E = \\displaystyle\\oint \\vec E \\cdot d\\vec A$. Unit: N·m²/C = V·m.',
        hindi: '$\\Phi_E = \\displaystyle\\oint \\vec E \\cdot d\\vec A$. इकाई: N·m²/C = V·m.',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Gauss\'s law',
        hindi: 'गॉस का नियम',
          },
          statement: {
            english: `Flux through any **closed surface** equals enclosed charge / $\\varepsilon_0$:
$$\\oint \\vec E \\cdot d\\vec A = \\dfrac{q_{\\text{enc}}}{\\varepsilon_0}.$$`,
            hindi: 'किसी भी **बंद सतह** से गुजरने वाला फ्लक्स बंद चार्ज के बराबर होता है / $\\varepsilon_0$: $$\\oint \\vec E \\cdot d\\vec A = \\dfrac{q_{\\text{enc}}}{\\varepsilon_0}.$$',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Infinite line charge** ($\\lambda$): $E = \\lambda/(2\\pi\\varepsilon_0 r)$, radial.',
        hindi: '**अनंत लाइन चार्ज** ($\\lambda$): $E = \\lambda/(2\\pi\\varepsilon_0 r)$, रेडियल।',
            },
            {
              english: '**Infinite sheet** ($\\sigma$): $E = \\sigma/(2\\varepsilon_0)$ on each side, perpendicular.',
        hindi: '**अनंत शीट** ($\\sigma$): $E = \\sigma/(2\\varepsilon_0)$ प्रत्येक तरफ, लंबवत।',
            },
            {
              english: '**Conducting sheet** (charge on one face): $E = \\sigma/\\varepsilon_0$ outside, $0$ inside.',
        hindi: '**कंडक्टिंग शीट** (एक चेहरे पर चार्ज): $E = \\sigma/\\varepsilon_0$ बाहर, $0$ अंदर।',
            },
            {
              english: '**Spherical shell** (radius $R$, charge $Q$): $E = kQ/r^2$ for $r > R$; $E = 0$ for $r < R$.',
        hindi: '**गोलाकार खोल** (त्रिज्या $R$, चार्ज $Q$): $r > R$ के लिए $E = kQ/r^2$; $E = 0$ के लिए $r < R$।',
            },
            {
              english: '**Solid sphere** (uniform $\\rho$): $E = kQr/R^3$ for $r < R$; $E = kQ/r^2$ outside.',
        hindi: '**ठोस गोला** (एकसमान $\\rho$): $r < R$ के लिए $E = kQr/R^3$; $E = kQ/r^2$ बाहर.',
            },
          ],
        },
      ],
    },
    {
      id: 'potential',
      title: {
        english: 'Electric Potential & Potential Energy',
        hindi: 'विद्युत विभव और विभव ऊर्जा',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Electric potential',
        hindi: 'विद्युतीय संभाव्यता',
          },
          body: {
            english: '$V(\\vec r) = -\\displaystyle\\int_\\infty^{\\vec r} \\vec E \\cdot d\\vec\\ell$. Work done by external agent (slowly) per unit charge to bring test charge from $\\infty$ to that point. Unit: volt.',
        hindi: '$V(\\vec r) = -\\displaystyle\\int_\\infty^{\\vec r} \\vec E \\cdot d\\vec\\ell$. परीक्षण चार्ज को $\\infty$ से उस बिंदु तक लाने के लिए प्रति यूनिट चार्ज बाहरी एजेंट द्वारा (धीरे-धीरे) किया गया कार्य। इकाई: वोल्ट.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Point charge**: $V = kq/r$.',
        hindi: '**प्वाइंट चार्ज**: $V = kq/r$.',
            },
            {
              english: '**Dipole**: $V = kp\\cos\\theta/r^2$ (along axis $\\theta = 0$).',
        hindi: '**द्विध्रुव**: $V = kp\\cos\\theta/r^2$ (अक्ष के अनुदिश $\\theta = 0$)।',
            },
            {
              english: '**Charged shell**: outside $V = kQ/r$; on/inside surface $V = kQ/R$ (constant inside).',
        hindi: '**चार्ज्ड शेल**: बाहर $V = kQ/r$; on/inside सतह $V = kQ/R$ (अंदर स्थिर)।',
            },
            {
              english: '**Solid sphere** (uniform): inside $V = kQ(3R^2 - r^2)/(2R^3)$; centre $V_c = 3kQ/(2R)$.',
        hindi: '**ठोस गोला** (एकसमान): अंदर $V = kQ(3R^2 - r^2)/(2R^3)$; केंद्र $V_c = 3kQ/(2R)$.',
            },
            {
              english: 'Relation: $\\vec E = -\\nabla V$; in 1D, $E_x = -dV/dx$.',
        hindi: 'संबंध: $\\vec E = -\\nabla V$; 1डी में, $E_x = -dV/dx$।',
            },
            {
              english: '**PE of two charges**: $U_{12} = kq_1 q_2/r_{12}$. For system: pairwise sum.',
        hindi: '**दो शुल्कों का पीई**: $U_{12} = kq_1 q_2/r_{12}$। सिस्टम के लिए: जोड़ीवार योग.',
            },
            {
              english: '**Equipotential surfaces** are perpendicular to $\\vec E$. No work done moving charge along equipotential.',
        hindi: '**समविभव सतहें** $\\vec E$ के लंबवत हैं। आवेश को समविभव के अनुदिश ले जाने में कोई कार्य नहीं किया गया।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Potential at the centre of a square of side $a$ with charges $+q, -q, +q, -q$ at corners.',
        hindi: '$a$ भुजा वाले एक वर्ग के केंद्र में विभव, कोनों पर $+q, -q, +q, -q$ आवेश के साथ।',
          },
          solution: {
            english: 'Pairs cancel symmetrically: $V_{\\text{centre}} = 0$. (Each $\\pm q$ at $a/\\sqrt 2$, sum = 0.)',
        hindi: 'जोड़े सममित रूप से रद्द होते हैं: $V_{\\text{centre}} = 0$। (प्रत्येक $\\pm q$ पर $a/\\sqrt 2$, sum = 0।)',
          },
        },
      ],
    },
    {
      id: 'conductors',
      title: {
        english: 'Conductors in Electrostatic Equilibrium',
        hindi: 'स्थिर विद्युत साम्य में चालक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Inside a conductor in electrostatic equilibrium: $\\vec E = 0$.',
        hindi: 'इलेक्ट्रोस्टैटिक संतुलन में एक कंडक्टर के अंदर: $\\vec E = 0$।',
            },
            {
              english: 'Net charge resides only on the **outer surface**.',
        hindi: 'नेट चार्ज केवल **बाहरी सतह** पर रहता है।',
            },
            {
              english: 'Just outside a conductor: $E = \\sigma/\\varepsilon_0$, perpendicular to surface.',
        hindi: 'एक कंडक्टर के ठीक बाहर: $E = \\sigma/\\varepsilon_0$, सतह के लंबवत।',
            },
            {
              english: 'Conductor surface is an **equipotential**.',
        hindi: 'कंडक्टर सतह एक **समविभव** है।',
            },
            {
              english: '**Electrostatic shielding**: cavity inside a conductor is shielded from external fields (Faraday cage).',
        hindi: '**इलेक्ट्रोस्टैटिक परिरक्षण**: एक कंडक्टर के अंदर की गुहा बाहरी क्षेत्रों (फैराडे केज) से परिरक्षित होती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'energy-density',
      title: {
        english: 'Energy of System & Energy Density',
        hindi: 'निकाय की ऊर्जा और ऊर्जा घनत्व',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Self-energy of a uniformly charged shell of radius $R$, charge $Q$: $U = \\dfrac{kQ^2}{2R}$.',
        hindi: 'त्रिज्या $R$, चार्ज $Q$: $U = \\dfrac{kQ^2}{2R}$ के एक समान रूप से चार्ज किए गए शेल की स्व-ऊर्जा।',
            },
            {
              english: 'Self-energy of uniformly charged solid sphere: $U = \\dfrac{3 k Q^2}{5 R}$.',
        hindi: 'समान रूप से आवेशित ठोस गोले की स्व-ऊर्जा: $U = \\dfrac{3 k Q^2}{5 R}$।',
            },
            {
              english: '**Electric energy density** at a point in vacuum: $u_E = \\tfrac12 \\varepsilon_0 E^2$ (J/m³).',
        hindi: '**विद्युत ऊर्जा घनत्व** निर्वात में एक बिंदु पर: $u_E = \\tfrac12 \\varepsilon_0 E^2$ (J/m³)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'F = kq_1q_2/r^2,\\ k = 9\\times 10^9',
      desc: {
        english: 'Coulomb',
        hindi: 'कूलम्ब',
      },
    },
    {
      latex: 'E = kq/r^2,\\ V = kq/r',
      desc: {
        english: 'Point charge',
        hindi: 'प्वाइंट चार्ज',
      },
    },
    {
      latex: 'E_{\\text{ax}} = 2kp/r^3,\\ E_{\\text{eq}} = kp/r^3',
      desc: {
        english: 'Dipole',
        hindi: 'द्विध्रुव',
      },
    },
    {
      latex: '\\oint \\vec E\\cdot d\\vec A = q_{\\text{enc}}/\\varepsilon_0',
      desc: {
        english: 'Gauss',
        hindi: 'गॉस',
      },
    },
    {
      latex: 'E_{\\text{sheet}} = \\sigma/(2\\varepsilon_0)',
      desc: {
        english: 'Infinite sheet',
        hindi: 'अनंत शीट',
      },
    },
    {
      latex: 'U = -\\vec p\\cdot\\vec E',
      desc: {
        english: 'Dipole PE',
        hindi: 'डिपोल पीई',
      },
    },
    {
      latex: 'u_E = \\tfrac12\\varepsilon_0 E^2',
      desc: {
        english: 'Energy density',
        hindi: 'ऊर्जा घनत्व',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Gauss',
      problem: {
        english: 'Charge $Q$ enclosed by a cube. Flux through one face?',
        hindi: 'एक घन से घिरा चार्ज $Q$। एक चेहरे के माध्यम से प्रवाह?',
      },
      answer: {
        english: '$Q/(6\\varepsilon_0)$',
        hindi: '$Q/(6\\varepsilon_0)$',
      },
      solution: {
        english: 'Total flux $Q/\\varepsilon_0$ symmetrically across 6 faces.',
        hindi: 'कुल प्रवाह $Q/\\varepsilon_0$ 6 फलकों पर सममित रूप से।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Dipole',
      problem: {
        english: 'Electric field on the axis of a dipole at $r = 0.1$ m, $p = 10^{-9}$ C·m. ($k = 9\\times 10^9$)',
        hindi: '$r = 0.1$ m, $p = 10^{-9}$ C·m पर एक द्विध्रुव के अक्ष पर विद्युत क्षेत्र। ($k = 9\\times 10^9$)',
      },
      answer: {
        english: '$1.8\\times 10^4$ N/C',
        hindi: '$1.8\\times 10^4$ N/C',
      },
      solution: {
        english: '$E = 2kp/r^3 = 2(9\\times 10^9)(10^{-9})/10^{-3}$.',
        hindi: '$E = 2kp/r^3 = 2(9\\times 10^9)(10^{-9})/10^{-3}$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Potential',
      problem: {
        english: 'Three charges $+q, -q, +q$ are placed at vertices of equilateral triangle of side $a$. Potential at centroid (distance $a/\\sqrt 3$ from each vertex).',
        hindi: 'तीन आवेश $+q, -q, +q$ भुजा $a$ वाले समबाहु त्रिभुज के शीर्षों पर रखे गए हैं। केन्द्रक पर विभव (प्रत्येक शीर्ष से दूरी $a/\\sqrt 3$)।',
      },
      answer: {
        english: '$kq\\sqrt 3/a$',
        hindi: '$kq\\sqrt 3/a$',
      },
      solution: {
        english: 'Sum of potentials: $k(q-q+q)\\sqrt 3/a = k q\\sqrt 3/a$.',
        hindi: 'संभावनाओं का योग: $k(q-q+q)\\sqrt 3/a = k q\\sqrt 3/a$.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Shell',
      problem: {
        english: 'Two concentric shells of radii $R$ and $2R$ carry charges $+Q$ and $-Q$. Potential at $r = 1.5R$?',
        hindi: 'त्रिज्या $R$ और $2R$ के दो संकेंद्रित गोले $+Q$ और $-Q$ आवेश वहन करते हैं। $r = 1.5R$ पर संभावना?',
      },
      answer: {
        english: '$V = kQ/(1.5R) - kQ/(2R) = kQ/(6R)$',
        hindi: '$V = kQ/(1.5R) - kQ/(2R) = kQ/(6R)$',
      },
      solution: {
        english: 'Inner contributes $kQ/(1.5R)$; outer (point inside it): $-kQ/(2R)$.',
        hindi: 'आंतरिक योगदान $kQ/(1.5R)$; बाहरी (इसके अंदर बिंदु): $-kQ/(2R)$.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Coulomb',
      problem: {
        english: 'Force between $+2\\mu C$ and $-3\\mu C$ at 30 cm apart.',
        hindi: '$+2\\mu C$ और $-3\\mu C$ के बीच 30 सेमी की दूरी पर बल लगाएं।',
      },
      answer: {
        english: '$0.6$ N (attractive)',
        hindi: '$0.6$ N (आकर्षक)',
      },
      solution: {
        english: '$F = 9\\times 10^9 \\cdot 6\\times 10^{-12}/0.09 = 0.6$ N.',
        hindi: '$F = 9\\times 10^9 \\cdot 6\\times 10^{-12}/0.09 = 0.6$ एन.',
      },
    },
  ],
  next: {
    slug: 'capacitance',
    title: 'Capacitance',
  },
};

export default chapter;
